import { useAsync } from "@react-hookz/web";
import type { SpeechifyPlayerConfig } from "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs";
import { useSetAtom } from "jotai";
import type { FunctionComponent } from "react";
import { useEffect, useRef, useState } from "react";

import {
  useBuildSpeechifyConfig,
  useBuildSpeechifyEventHandler,
} from "~/components/article/article-speech/article-speech-player/hooks";
import { useArticleSpeechContext } from "~/components/article/article-speech/contexts";
import { onlyOnePlayingPlayerAtom } from "~/lib/only-one-playing-player";

import { InlinePlayerContainer } from "./styles";

export type Variants = "posties" | "default";

type Props = {
  articleElementReference: HTMLElement | null;
  articleEntityId: string;
  variant?: Variants;
};

export const ArticleSpeechPlayer: FunctionComponent<Props> = ({
  articleElementReference,
  articleEntityId,
  variant = "default",
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const { setArticleSpeechifyPlayer } = useArticleSpeechContext();

  const onePlayerDispatch = useSetAtom(onlyOnePlayingPlayerAtom);
  const { speechifyEventHandler } = useBuildSpeechifyEventHandler();

  const inlinePlayerReference = useRef<HTMLDivElement>(null);
  const inlinePLayerId = `article-${articleEntityId}`;

  const { config: speechifyConfig } = useBuildSpeechifyConfig({
    contentParentElement: articleElementReference,
    inlinePLayerId,
    inlinePlayerReference,
    variant,
  });

  const [speechifyWidgetState, prepareSpeechifyWidget] = useAsync(
    async (speechifyConfig: Partial<SpeechifyPlayerConfig>) => {
      const speechifyWidget = await import(
        /* webpackIgnore: true */ "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs"
      );
      const widget = speechifyWidget.makeSpeechifyExperience(speechifyConfig);
      const removeEvents = widget.addEventListener(speechifyEventHandler);

      return {
        removeEvents,
        widget,
      };
    },
  );

  useEffect(() => {
    if (!articleElementReference || !speechifyConfig || isMounted) return;

    void prepareSpeechifyWidget.execute(speechifyConfig);
  }, [articleElementReference, isMounted, prepareSpeechifyWidget, speechifyConfig]);

  useEffect(() => {
    if (
      isMounted ||
      !speechifyWidgetState.result?.widget ||
      !speechifyWidgetState.result?.removeEvents
    )
      return;

    const speechifyWidget = speechifyWidgetState.result?.widget;

    const initMountWidget = async () => {
      setIsMounted(true);

      await speechifyWidget.mount();

      setArticleSpeechifyPlayer?.(speechifyWidget);
      onePlayerDispatch({
        payload: {
          articleSpeechifyPlayer: speechifyWidget,
        },
        type: "loadArticleSpeechPlayer",
      });
    };

    void initMountWidget();

    return () => {
      speechifyWidgetState.result?.removeEvents();
      void speechifyWidgetState?.result?.widget.unmount();
    };
  }, [
    isMounted,
    onePlayerDispatch,
    setArticleSpeechifyPlayer,
    speechifyWidgetState.result,
    speechifyWidgetState.result?.removeEvents,
    speechifyWidgetState.result?.widget,
  ]);

  return <InlinePlayerContainer id={inlinePLayerId} ref={inlinePlayerReference} />;
};
